import { cva, type VariantProps } from "@msuite/ui/libs/cva";
import type { MenuItems } from "@msuite/ui/menu";
import { cn } from "@msuite/ui/utils";
import { guardArray } from "@msuite/utils/array";
import type { Nullable, ProjectCard as ProjectCardType, Tables } from "@msuite/utils/types";
import { useMemo } from "react";
import { TicketCard } from "../ticket-card";
import type { ExtendTicketCardProps, TicketCardProps } from "../ticket-card/ticket-card";
import { ProjectCardCompact } from "./project-card-compact";
import { ProjectCardContextMenu } from "./project-card-context-menu";
import { ProjectInformation } from "./project-information";

/** Props Interface */
export interface ProjectCardProps extends VariantProps<typeof projectCard> {
	project: Nullable<
		Omit<ProjectCardType, "_tickets" | "createdAt" | "updatedAt"> & {
			createdAt: string | number | null;
			updatedAt: string | number | null;
		}
	>;
	// The card's own ticket shape, so a synced row carrying its type reaches the card intact.
	tickets?: TicketCardProps["ticket"][];
	isTicketDraggable?: boolean;
	ticketProps?: ExtendTicketCardProps;
	ticketWrapper?: (ticket: Tables<"tickets">, children: React.ReactNode) => React.ReactNode;
	withNote?: boolean;
	contextMenuItems?: MenuItems;
	/**
	 * Off on a guest board, where the project is the host's: its menu would offer editing the
	 * project, adding a ticket and deactivating it, none of which is the guest's to do. The card
	 * then renders without the menu around it, as `TicketCard` does.
	 */
	withContextMenu?: boolean;
}

export function ProjectCard({
	project,
	tickets,
	isTicketDraggable = true,
	ticketProps,
	ticketWrapper,
	withNote = false,
	variant,
	contextMenuItems,
	withContextMenu = true,
}: ProjectCardProps) {
	/** Classes */
	const classes = projectCard({ variant });
	const _tickets = useMemo(() => {
		return tickets?.toSorted((a, b) => {
			return a.number.localeCompare(b.number);
		});
	}, [tickets]);

	/** Compact View */
	if (!project) return null;
	if (variant === "compact") return <ProjectCardCompact project={project} />;

	/** Render */
	const content = (
		<div
			id={`project-card-${project?.id}`}
			className={cn(classes)}
			style={{
				contentVisibility: "auto",
			}}
		>
			<ProjectInformation
				project={project}
				members={project?._members}
				client={project?._client}
				withNote={withNote}
				workspace={project?._workspace}
			/>
			{guardArray(_tickets).length > 0 && (
				<div className="v-stack gap-0.5">
					{_tickets?.map((ticket) => {
						const card = (
							<TicketCard
								key={ticket.id}
								ticket={ticket}
								isDraggable={isTicketDraggable}
								{...(ticketProps ?? {})}
							/>
						);
						return ticketWrapper ? ticketWrapper(ticket, card) : card;
					})}
				</div>
			)}
		</div>
	);
	if (!withContextMenu) return content;
	return (
		<ProjectCardContextMenu
			projectId={project?.id}
			project={project}
			items={contextMenuItems}
		>
			{content}
		</ProjectCardContextMenu>
	);
}

const projectCard = cva(["relative flex flex-col gap-1"], {
	variants: {
		variant: {
			default: ["card project-card"],
			plain: [],
			compact: [],
		},
	},
	defaultVariants: {
		variant: "default",
	},
});
